/** Everything a page says about itself to a crawler, a link preview and the browser's tab. */
export interface PageMetadata {
    title: string;
    description?: string | null;
    /** Root-relative. The `hreflang` alternates that go with it, per language. */
    canonicalPath?: string;
    alternatePaths?: Record<string, string>;
    imageUrl?: string | null;
    /** `article` for a blog post, `website` for everything else. */
    type?: 'article' | 'website';
    publishedAt?: string;
    modifiedAt?: string;
}

/** One tag to put in the head, as data rather than as a DOM node or a string. */
export interface HeadTag {
    element: 'meta' | 'link';
    attributes: Readonly<Record<string, string>>;
}

/**
 * The tags a page's metadata becomes.
 *
 * **One description, two consumers, and that is the point.** `usePageMetadata` turns these into DOM
 * nodes after the bundle loads; the build-time prerenderer serialises the same list into the served
 * HTML. The plan noted that the hook was *deliberately* the same tag set a renderer would emit —
 * this is what stops that being a matter of somebody remembering.
 *
 * @param origin The absolute origin to resolve root-relative paths against. A canonical link has to
 * be absolute, and at build time there is no `window.location` to read it from.
 */
export function headTagsFor(metadata: PageMetadata, origin: string): HeadTag[] {
    const { title, description, canonicalPath, alternatePaths, imageUrl, type = 'website' } = metadata;
    const tags: HeadTag[] = [];

    push(tags, 'meta', { name: 'description', content: description });
    push(tags, 'meta', { property: 'og:title', content: title });
    push(tags, 'meta', { property: 'og:description', content: description });
    push(tags, 'meta', { property: 'og:type', content: type });
    push(tags, 'meta', { property: 'og:image', content: absolute(origin, imageUrl) });
    push(tags, 'meta', { name: 'twitter:card', content: imageUrl ? 'summary_large_image' : 'summary' });

    if (type === 'article') {
        push(tags, 'meta', { property: 'article:published_time', content: metadata.publishedAt });
        push(tags, 'meta', { property: 'article:modified_time', content: metadata.modifiedAt });
    }

    const canonical = absolute(origin, canonicalPath);

    if (canonical !== undefined) {
        push(tags, 'link', { rel: 'canonical', href: canonical });
        push(tags, 'meta', { property: 'og:url', content: canonical });
    }

    for (const [language, path] of Object.entries(alternatePaths ?? {})) {
        push(tags, 'link', { rel: 'alternate', hreflang: language, href: absolute(origin, path) });
    }

    return tags;
}

/** The same tags as HTML, for the prerenderer. */
export function renderHeadTags(tags: readonly HeadTag[], indent = '        '): string {
    return tags
        .map((tag) => {
            const attributes = Object.entries(tag.attributes)
                .map(([name, value]) => `${name}="${escapeAttribute(value)}"`)
                .join(' ');

            return `${indent}<${tag.element} ${attributes} />`;
        })
        .join('\n');
}

/**
 * Escapes a value for an HTML attribute.
 *
 * Every value reaching this comes from a translation table or a slug today, but an article's title
 * will reach it tomorrow — and a title is whatever an editor typed. Escaping at the boundary rather
 * than trusting the source is the cheaper half of that decision.
 */
export function escapeAttribute(value: string): string {
    return value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function push(tags: HeadTag[], element: 'meta' | 'link', attributes: Record<string, string | null | undefined>): void {
    const filled: Record<string, string> = {};

    for (const [name, value] of Object.entries(attributes)) {
        // An empty value is not a tag. `<meta name="description" content="">` tells a crawler the
        // page has no description, which is worse than not claiming one.
        if (value === null || value === undefined || value.length === 0) {
            return;
        }

        filled[name] = value;
    }

    tags.push({ element, attributes: filled });
}

function absolute(origin: string, path: string | null | undefined): string | undefined {
    if (path === null || path === undefined || path.length === 0) {
        return undefined;
    }

    return path.startsWith('http') ? path : `${origin}${path}`;
}
